// Navigation (RK-SYS-005 section 3). Phone: bottom bar with five tabs and RK Intelligence raised
// in the centre. Desktop: left sidebar, global search on top, RK Intelligence docked on the right
// of every page above 1024 px (REQ-UX-12). The sync indicator is always visible (REQ-UX-06).
import { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Sun, ShoppingBasket, Sparkles, PawPrint, Menu, Inbox, HelpCircle, ClipboardCheck, ListTodo, BarChart3, Map,
  Settings, HeartPulse, Search, Plus, FileText, Truck, Banknote, MoveRight, Hash, Baby, LogOut as ExitIcon, CircleHelp
} from "lucide-react";
import { cx } from "../ui/primitives";
import { Sheet } from "../ui/sheet";
import { SyncIndicator } from "../offline/SyncIndicator";
import { ReadinessIndicator } from "../offline/ReadinessIndicator";
import { UpdateBar } from "../offline/UpdateBar";
import { useAuth } from "../auth/AuthProvider";
import { useMediaQuery } from "../lib/useMediaQuery";
import { HorizontalLogo, RkIcon } from "../brand/Brand";
import { searchAll, searchKey } from "../lib/search";
import { DOCUMENT_ITEMS } from "../lib/docsData";

const ChatPanel = lazy(() => import("../screens/chat/ChatPanel"));

export const MORE_ITEMS = [
  { to: "/inbox", label: "Inbox", icon: Inbox },
  // Decision 89 #5: payments reached in two taps on a phone (More > Payments), one on a desktop (the sidebar).
  { to: "/payments", label: "Payments", icon: Banknote },
  { to: "/queries", label: "Queries", icon: HelpCircle },
  { to: "/proposals", label: "Cards", icon: ClipboardCheck },
  { to: "/tasks", label: "Tasks", icon: ListTodo },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/map", label: "Map", icon: Map },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/health", label: "Health", icon: HeartPulse }
];

const TABS = [
  { to: "/today", label: "Today", icon: Sun },
  { to: "/purchases", label: "Purchases", icon: ShoppingBasket },
  { to: "/chat", label: "RK Intelligence", icon: Sparkles, centre: true },
  { to: "/animals", label: "Animals", icon: PawPrint },
  { to: "/more", label: "More", icon: Menu }
];

export const QUICK_ACTIONS = [
  { to: "/receive", label: "Receive delivery", icon: Truck },
  { to: "/ready-to-pay", label: "Pay or record a payment", icon: Banknote },
  { to: "/quick/move", label: "Move", icon: MoveRight },
  { to: "/quick/count", label: "Game count or observation", icon: Hash },
  { to: "/quick/calf", label: "Calf born", icon: Baby },
  { to: "/quick/calf-tag", label: "Tag a calf", icon: Baby },
  { to: "/quick/tag-counted", label: "Catch and tag from a herd", icon: Hash },
  { to: "/quick/exit", label: "Death, sale or harvest", icon: ExitIcon },
  { to: "/quick/missing", label: "Missing", icon: CircleHelp },
  { to: "/quick/found", label: "Found", icon: CircleHelp }
];

/**
 * Decision 142 item 12: the height of a bar of the shell, kept in a CSS variable on the page (--rk-top-bar is the sticky
 * top bar with the update bar, --rk-bottom-bar the phone's tab bar), so a screen laid out between the bars (the chat) is
 * anchored to the screen itself, whatever the window's scroll, and follows the bars when they change height (the safe
 * area, a new version waiting).
 */
function useBarHeight<T extends HTMLElement>(name: string) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty(name, `${el.getBoundingClientRect().height}px`);
    set();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [name]);
  return ref;
}

function BottomBar() {
  const bar = useBarHeight<HTMLElement>("--rk-bottom-bar");
  return (
    <nav ref={bar} aria-label="Main" className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur safe-bottom lg:hidden">
      <ul className="mx-auto grid max-w-xl grid-cols-5">
        {TABS.map((t) => (
          <li key={t.to} className="flex justify-center">
            <NavLink to={t.to} aria-label={t.label}
              className={({ isActive }) => cx("flex min-h-[60px] w-full flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition",
                t.centre ? "-mt-5" : "", isActive ? "font-bold text-ink" : "text-muted")}>
              {({ isActive }) => t.centre ? (
                <>
                  <span className={cx("flex h-14 w-14 items-center justify-center rounded-full shadow-raised ring-4 ring-bg transition",
                    isActive ? "bg-accent text-on-accent" : "bg-primary text-on-primary")}>
                    <t.icon className="h-6 w-6" aria-hidden />
                  </span>
                  <span className={cx("mt-0.5", isActive ? "font-bold text-ink" : "text-ink")}>RK</span>
                </>
              ) : (
                <>
                  {/* Active tab: a Dune pill behind the icon and a bold label (never colour alone; BD-79). */}
                  <span className={cx("flex h-8 w-14 items-center justify-center rounded-full transition", isActive ? "bg-accent text-on-accent" : "")}>
                    <t.icon className="h-6 w-6" aria-hidden strokeWidth={isActive ? 2.25 : 1.75} />
                  </span>
                  <span>{t.label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function SideBar() {
  const { me, signOut } = useAuth();
  const items = [...TABS.filter((t) => t.to !== "/more"), ...MORE_ITEMS];
  // The Documents group stands right after Payments, so it is on the first screen of the sidebar (design review S7).
  const groupAt = items.findIndex((t) => t.to === "/payments") + 1;
  const sideItem = (t: (typeof items)[number]) => (
    <li key={t.to}>
      <NavLink to={t.to}
        className={({ isActive }) => cx("flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-[15px] transition",
          isActive ? "bg-accent text-on-accent font-bold" : "text-ink/80 hover:bg-ink/5")}>
        <t.icon className="h-5 w-5" aria-hidden strokeWidth={1.75} />
        {t.label}
      </NavLink>
    </li>
  );
  return (
    <nav aria-label="Main" className="hidden lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[248px] lg:self-start shrink-0 flex-col border-r border-line bg-surface/70 px-3 py-5">
      <div className="px-3 pb-6">
        <HorizontalLogo on="surface" className="h-[50px] w-[179px]" />
      </div>
      <ul className="flex-1 space-y-0.5 overflow-y-auto">
        {items.slice(0, groupAt).map(sideItem)}
        <li data-testid="menu-documents" className="py-3">
          <h2 className="px-3 pb-1 text-[12px] font-semibold uppercase tracking-wider text-muted">Documents</h2>
          <ul className="space-y-0.5">
            {DOCUMENT_ITEMS.map((d) => (
              <li key={d.to}>
                <NavLink to={d.to}
                  className={({ isActive }) => cx("flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-[15px] transition",
                    isActive ? "bg-accent text-on-accent font-bold" : "text-ink/80 hover:bg-ink/5")}>
                  <FileText className="h-5 w-5" aria-hidden strokeWidth={1.75} />
                  {d.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </li>
        {items.slice(groupAt).map(sideItem)}
      </ul>
      <div className="border-t border-line pt-3 px-3 text-[14px]">
        <div className="font-medium truncate">{me?.full_name}</div>
        <button type="button" onClick={() => void signOut()} className="mt-1 min-h-[44px] text-muted hover:text-ink">Sign out</button>
      </div>
    </nav>
  );
}

// Decision 87 #1: the top bar finds everything. Typing shows the grouped results (the Search screen, updated as you
// type, without a new history entry per letter); Enter opens the top result, the best match the database returns, or
// shows "Nothing found." on the Search screen.
function TopBar() {
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearch = useRef(false);
  onSearch.current = loc.pathname === "/search";
  const cancel = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  useEffect(() => cancel, []);
  // Decision 126 h (the audit: "the top search box keeps the last words typed after you leave the search"): leaving
  // Search, for a card or any screen, empties the box; on Search it shows the words searched for.
  const searched = loc.pathname === "/search" ? new URLSearchParams(loc.search).get("q") ?? "" : null;
  useEffect(() => {
    if (searched === null) { cancel(); setQ(""); } else setQ((now) => (now.trim() ? now : searched));
  }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const typed = (v: string) => {
    setQ(v);
    cancel();
    const words = v.trim();
    if (!words) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      nav(`/search?q=${encodeURIComponent(words)}`, { replace: onSearch.current });
    }, 250);
  };
  const submit = async (words: string) => {
    cancel();
    if (!words) return;
    let top: string | null = null;
    try {
      const hits = await qc.fetchQuery({ queryKey: searchKey(words), queryFn: () => searchAll(words) });
      top = hits[0]?.link ?? null;
    } catch { top = null; }
    nav(top ?? `/search?q=${encodeURIComponent(words)}`);
  };
  return (
    <div className="flex items-center gap-2 border-b border-line/60 px-4 py-2 safe-top lg:px-8">
      <RkIcon on="background" label="Royal Kalahari" className="h-7 w-[44px] shrink-0 lg:hidden" />
      <form role="search" className="min-w-0 flex-1" onSubmit={(e) => {
        e.preventDefault();
        const box = e.currentTarget.querySelector("input");
        void submit((box?.value ?? q).trim());
      }}>
        {/* Decision 126 h: 16 px, as every other box of the app, so iPhone Safari never zooms the page in on it. */}
        <label className="relative block max-w-xl">
          <span className="sr-only">Search everything: animals, tags, invoices, lots, payments, documents, cards, queries, sellers</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
          <input value={q} onChange={(e) => typed(e.target.value)} placeholder="Search" enterKeyHint="search"
            className="w-full min-h-[44px] rounded-full border border-line bg-surface pl-9 pr-4 text-[16px] focus:outline-none focus:ring-2 focus:ring-primary/30" />
        </label>
      </form>
      <SyncIndicator />
      <ReadinessIndicator />
    </div>
  );
}

function QuickActionsButton() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Quick actions"
        className="fixed bottom-24 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-on-accent shadow-raised transition hover:brightness-110 lg:bottom-8 lg:right-[420px]">
        <Plus className="h-7 w-7" aria-hidden />
      </button>
      <Sheet open={open} onOpenChange={setOpen} title="Quick actions" description="These work without signal.">
        <ul className="grid grid-cols-2 gap-2 pb-2 sm:grid-cols-3">
          {QUICK_ACTIONS.map((a) => (
            <li key={a.to}>
              <button type="button" onClick={() => { setOpen(false); nav(a.to); }}
                className="flex w-full min-h-[88px] flex-col items-start justify-between rounded-card border border-line bg-bg p-3 text-left hover:border-primary/40">
                <a.icon className="h-6 w-6 text-primary" aria-hidden />
                <span className="font-medium">{a.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </Sheet>
    </>
  );
}

// Owner's decision 31 (BD-150): CEO test mode, on every build but the live one. The guard is decided when the app is
// built, so the live bundle holds none of its code (tests/unit/ceo-test-mode-live.test.ts).
const CeoTestPanel = import.meta.env.VITE_ENVIRONMENT !== "live" ? lazy(() => import("../testmode/Panel")) : null;

export function AppShell({ children }: { children?: ReactNode }) {
  const loc = useLocation();
  const wide = useMediaQuery("(min-width: 1024px)");
  const onChat = loc.pathname.startsWith("/chat");
  const showFab = /^\/(purchases|herd)/.test(loc.pathname);
  // Decision 142 item 12: the top bar and the update bar are one sticky block, whose height the chat is laid out under.
  const top = useBarHeight<HTMLDivElement>("--rk-top-bar");
  return (
    <div className="flex min-h-dvh">
      <SideBar />
      <div className="flex min-w-0 flex-1 flex-col">
        <div ref={top} className="sticky top-0 z-20 bg-bg/90 backdrop-blur">
          <TopBar />
          {/* Decision 103 a: a new version waiting shows here on every screen; one tap reloads, never on its own. */}
          <UpdateBar />
        </div>
        <main id="main" className={onChat ? "flex-1 p-0" : "flex-1 px-4 pb-32 pt-5 lg:px-8 lg:pb-10"}>
          <div className={cx(!onChat && "mx-auto max-w-5xl")}>{children ?? <Outlet />}</div>
        </main>
      </div>
      {wide && !onChat && (
        <aside aria-label="RK Intelligence" data-testid="docked-chat" className="sticky top-0 hidden h-dvh w-[400px] shrink-0 border-l border-line bg-surface lg:flex lg:flex-col">
          <Suspense fallback={null}><ChatPanel docked /></Suspense>
        </aside>
      )}
      {showFab && <QuickActionsButton />}
      <BottomBar />
      {CeoTestPanel && <Suspense fallback={null}><CeoTestPanel /></Suspense>}
    </div>
  );
}
